/**
 * Collection Ajax Add-to-Cart (use EXISTING theme bubble only)
 * Fixes:
 * - Never writes into .cart-bubble__background (no duplicate numbers)
 * - Does NOT reset inline styles (prevents white/white state)
 * - Re-applies count after delays to survive theme header re-renders
 */

const CART_ADD_URL = (window.routes && window.routes.cart_add_url) || "/cart/add.js";
const CART_JSON_URL = (window.routes && window.routes.cart_url) || "/cart.js";

async function fetchCart() {
  const res = await fetch(CART_JSON_URL, {
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error("Failed to fetch cart");
  return res.json();
}

function getHeaderCartLinks() {
  const links = Array.from(document.querySelectorAll("a.header-actions__cart-icon"));
  if (links.length) return links;

  // fallback
  return Array.from(document.querySelectorAll('a[href="/cart"], a[href$="/cart"]'));
}

function updateBubbleInLink(cartLink, itemCount) {
  if (!cartLink) return false;

  const bg = cartLink.querySelector(".cart-bubble__background");
  const bubble = cartLink.querySelector(".cart-bubble") || (bg ? bg.parentElement : null);
  if (!bubble) return false;

  const next = String(itemCount);

  const spans = Array.from(bubble.querySelectorAll("span")).filter(
    (s) => !s.classList.contains("cart-bubble__background")
  );

  let countSpan = spans.find((s) => /^[0-9]+$/.test((s.textContent || "").trim()));

  if (countSpan) {
    countSpan.textContent = next;
  } else {
    countSpan = spans.find((s) => (s.textContent || "").trim() === "");
    if (countSpan) {
      countSpan.textContent = next;
    }
  }

  if (!countSpan && spans[0]) {
    countSpan = spans[0];
    countSpan.textContent = next;
  }

  if (countSpan) {
    countSpan.classList.toggle("hidden", itemCount === 0);
  }

  bubble.classList.toggle("visually-hidden", itemCount === 0);
  cartLink.classList.toggle("header-actions__cart-icon--has-cart", itemCount > 0);

  if (itemCount > 0) {
    bubble.hidden = false;
    bubble.removeAttribute("hidden");
    bubble.setAttribute("aria-hidden", "false");
  } else {
    bubble.setAttribute("aria-hidden", "true");
  }

  bubble.setAttribute("data-count", next);
  bubble.setAttribute("data-cart-count", next);

  return !!countSpan;
}

function updateExistingCartBubbles(itemCount) {
  const links = getHeaderCartLinks();
  let any = false;

  links.forEach((link) => {
    if (updateBubbleInLink(link, itemCount)) any = true;
  });

  const next = String(itemCount);
  const fallbackSelectors = [
    "[data-cart-count]",
    ".cart-count-bubble span",
    ".count-bubble span",
    "#cart-icon-bubble span",
  ];

  fallbackSelectors.forEach((sel) => {
    document.querySelectorAll(sel).forEach((el) => {
      const t = (el.textContent || "").trim();
      if (t === "" || /^[0-9]+$/.test(t)) el.textContent = next;
    });
  });

  return any;
}

function dispatchCartEvents(cart) {
  document.dispatchEvent(
    new CustomEvent("cart:update", {
      bubbles: true,
      detail: {
        resource: cart,
        sourceId: "custom-collection",
        data: {
          itemCount: cart.item_count || 0,
          source: "collection-add-to-cart",
        },
      },
    })
  );

  const detail = { cart };
  document.dispatchEvent(new CustomEvent("cart:refresh", { detail }));
  document.dispatchEvent(new CustomEvent("cart:updated", { detail }));
  window.dispatchEvent(new CustomEvent("cart:refresh", { detail }));
}

async function ajaxAddToCart(form) {
  const button = form.querySelector("button[type='submit'], .collection-card-cta__button");
  const originalText = button ? button.textContent : "";
  const formData = new FormData(form);

  if (button) {
    button.disabled = true;
    button.textContent = "Adding...";
  }

  const res = await fetch(CART_ADD_URL, {
    method: "POST",
    headers: { Accept: "application/json" },
    body: formData,
    credentials: "same-origin",
  });

  if (!res.ok) {
    if (button) {
      button.textContent = "Error";
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 1500);
    }
    throw new Error("Add to cart failed");
  }

  if (button) {
    button.textContent = "Added!";
    setTimeout(() => {
      button.textContent = originalText;
      button.disabled = false;
    }, 1500);
  }

  try {
    const cart = await fetchCart();

    // Apply immediately + re-apply after likely theme re-render timings
    updateExistingCartBubbles(cart.item_count);
    setTimeout(() => updateExistingCartBubbles(cart.item_count), 150);
    setTimeout(() => updateExistingCartBubbles(cart.item_count), 600);
    setTimeout(() => updateExistingCartBubbles(cart.item_count), 1200);

    dispatchCartEvents(cart);
  } catch (e) {
    console.warn("[custom-collection] cart update failed", e);
  }
}

document.addEventListener("DOMContentLoaded", async () => {
  try {
    const cart = await fetchCart();
    updateExistingCartBubbles(cart.item_count);
  } catch (_) {}
});

document.addEventListener("submit", (e) => {
  const form =
    (e.target && e.target.matches && e.target.matches("form[data-ajax-add-to-cart]") && e.target) ||
    (e.target && e.target.closest && e.target.closest("form[data-ajax-add-to-cart]"));

  if (!form) return;

  e.preventDefault();
  ajaxAddToCart(form).catch(() => {});
});
/* =========================================================
   Desktop + Mobile: Replace price min/max fields with dual slider
   - Desktop: sidebar facets (.facets--vertical)
   - Mobile: drawer facets (dialog.facets--drawer) — appears only when Filters opened
   - Survives AJAX facet re-renders via MutationObserver
   ========================================================= */
(function () {
  function clamp(n, a, b) {
    return Math.min(b, Math.max(a, n));
  }

  function fmtGBP(v) {
    return `£${Number(v).toFixed(2)}`;
  }

  function findPriceInputs(root) {
    const min =
      root.querySelector('input[name="filter.v.price.gte"]') ||
      root.querySelector('input[name*="price.gte"]');

    const max =
      root.querySelector('input[name="filter.v.price.lte"]') ||
      root.querySelector('input[name*="price.lte"]');

    if (!min || !max) return null;
    return { min, max };
  }

  function findCommonWrapper(minInput, maxInput) {
    let el = minInput.parentElement;
    while (el && el !== document.body) {
      if (el.contains(maxInput)) return el;
      el = el.parentElement;
    }
    return null;
  }

  function getMaxAllowed(maxInput, groupText) {
    let maxAllowed = parseFloat(maxInput.max || "");
    if (Number.isFinite(maxAllowed) && maxAllowed > 0) return maxAllowed;

    const m = (groupText || "").match(/highest price is[^0-9]*([0-9]+(?:\.[0-9]+)?)/i);
    if (m) return parseFloat(m[1]);

    const v = parseFloat(maxInput.value || "");
    return Number.isFinite(v) && v > 0 ? v : 0;
  }

  function initPriceSliderWithin(root) {
    if (!root) return;

    const priceInputs = findPriceInputs(root);
    if (!priceInputs) return;

    const { min: minInput, max: maxInput } = priceInputs;

    const group =
      minInput.closest("details") ||
      minInput.closest("fieldset") ||
      minInput.closest(".facets__item") ||
      minInput.closest(".facet") ||
      minInput.parentElement;

    if (!group) return;
    if (group.querySelector(".ps-price-slider")) return; // already done

    const wrapper = findCommonWrapper(minInput, maxInput);
    if (!wrapper) return;

    const minAllowed = parseFloat(minInput.min || "0") || 0;
    const maxAllowed = getMaxAllowed(maxInput, group.textContent);
    if (!Number.isFinite(maxAllowed) || maxAllowed <= 0) return;

    const step = parseFloat(minInput.step || maxInput.step || "0.01") || 0.01;

    let vMin = minInput.value ? parseFloat(minInput.value) : minAllowed;
    let vMax = maxInput.value ? parseFloat(maxInput.value) : maxAllowed;

    vMin = clamp(vMin, minAllowed, maxAllowed);
    vMax = clamp(vMax, minAllowed, maxAllowed);

    // Hide native fields (in that root only)
    wrapper.style.display = "none";

    // Build slider UI
    const slider = document.createElement("div");
    slider.className = "ps-price-slider";
    slider.innerHTML = `
      <div class="ps-price-slider__values">
        <span data-min-label></span>
        <span data-max-label></span>
      </div>
      <div class="ps-price-slider__track">
        <div class="ps-price-slider__range" data-range></div>
        <input type="range" data-min />
        <input type="range" data-max />
      </div>
    `;

    wrapper.insertAdjacentElement("afterend", slider);

    const rMin = slider.querySelector('input[data-min]');
    const rMax = slider.querySelector('input[data-max]');
    const rangeEl = slider.querySelector("[data-range]");
    const minLabel = slider.querySelector("[data-min-label]");
    const maxLabel = slider.querySelector("[data-max-label]");

    [rMin, rMax].forEach((r) => {
      r.min = String(minAllowed);
      r.max = String(maxAllowed);
      r.step = String(step);
    });

    rMin.value = String(vMin);
    rMax.value = String(vMax);

    function paint() {
      if (parseFloat(rMin.value) > parseFloat(rMax.value) - step) {
        rMin.value = String(parseFloat(rMax.value) - step);
      }
      if (parseFloat(rMax.value) < parseFloat(rMin.value) + step) {
        rMax.value = String(parseFloat(rMin.value) + step);
      }

      const a = parseFloat(rMin.value);
      const b = parseFloat(rMax.value);

      minLabel.textContent = fmtGBP(a);
      maxLabel.textContent = fmtGBP(b);

      const leftPct = ((a - minAllowed) / (maxAllowed - minAllowed)) * 100;
      const rightPct = 100 - ((b - minAllowed) / (maxAllowed - minAllowed)) * 100;

      rangeEl.style.left = `${leftPct}%`;
      rangeEl.style.right = `${rightPct}%`;

      minInput.value = String(a);
      maxInput.value = String(b);
    }

    function commit() {
      minInput.dispatchEvent(new Event("input", { bubbles: true }));
      maxInput.dispatchEvent(new Event("input", { bubbles: true }));
      minInput.dispatchEvent(new Event("change", { bubbles: true }));
      maxInput.dispatchEvent(new Event("change", { bubbles: true }));
    }

    paint();

    rMin.addEventListener("input", paint);
    rMax.addEventListener("input", paint);

    rMin.addEventListener("change", () => { paint(); commit(); });
    rMax.addEventListener("change", () => { paint(); commit(); });
  }

  function boot() {
    // Desktop sidebar
    const desktopFacets = document.querySelector(".collection-page .facets--vertical") || document.querySelector(".facets--vertical");
    if (desktopFacets) {
      initPriceSliderWithin(desktopFacets);
      new MutationObserver(() => initPriceSliderWithin(desktopFacets)).observe(desktopFacets, { childList: true, subtree: true });
    }

    // Mobile drawer (only visible when Filters opened, but we can init even while hidden)
    const attachDrawerObserver = (dialog) => {
      if (!dialog || dialog.__psObserved) return;
      dialog.__psObserved = true;

      initPriceSliderWithin(dialog);

      new MutationObserver(() => initPriceSliderWithin(dialog)).observe(dialog, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["open"],
      });
    };

    const existingDialog = document.querySelector("dialog.facets--drawer");
    if (existingDialog) attachDrawerObserver(existingDialog);

    // If the drawer is injected later, watch for it
    new MutationObserver(() => {
      const dialog = document.querySelector("dialog.facets--drawer");
      if (dialog) attachDrawerObserver(dialog);
    }).observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener("DOMContentLoaded", boot);
})();
/* =========================================================
   Collection filter gallery repair

   Filtering morphs existing product cards in place. That can preserve an old
   carousel position while replacing the slide visibility attributes.

   This repair:
   - runs once initially;
   - runs after an actual filter-result DOM update;
   - resets each affected product card to its first non-hidden image;
   - does not run during ordinary dragging or swiping.
   ========================================================= */
(function () {
  function ensureImgSrc(img) {
    if (!img) return;

    const src = img.getAttribute('src') || '';
    const srcset = img.getAttribute('srcset') || '';

    const dataSrc = img.dataset && img.dataset.src;
    const dataSrcset =
      img.dataset && (img.dataset.srcset || img.dataset.srcSet);

    if ((!src || src.startsWith('data:image')) && dataSrc) {
      img.setAttribute('src', dataSrc);
    }

    if (!srcset && dataSrcset) {
      img.setAttribute('srcset', dataSrcset);
    }
  }

  function resetCardGallery(component) {
    const scroller = component.querySelector(
      'slideshow-container > slideshow-slides'
    );

    if (!scroller) return;

    const slides = Array.from(scroller.children).filter(
      (element) => element.matches('slideshow-slide')
    );

    if (!slides.length) return;

    const availableSlides = slides.filter(
      (slide) => !slide.hasAttribute('hidden')
    );

    if (!availableSlides.length) return;

    const firstAvailableSlide = availableSlides[0];

    slides.forEach((slide) => {
      slide.setAttribute(
        'aria-hidden',
        slide === firstAvailableSlide ? 'false' : 'true'
      );
    });

    /*
     * Keep the slideshow component's public current-slide state aligned.
     * This does not replace or disable its native swipe implementation.
     */
    if ('current' in component) {
      component.current = 0;
    }

    /*
     * Filtering may preserve the previous horizontal scroll position.
     * Reset to the beginning, where the first non-hidden slide is laid out.
     */
    scroller.scrollLeft = 0;

    requestAnimationFrame(() => {
      if (component.isConnected) {
        scroller.scrollLeft = 0;
      }
    });

    component.querySelectorAll('img').forEach(ensureImgSrc);

    component.querySelectorAll('source').forEach((source) => {
      const srcset = source.getAttribute('srcset') || '';
      const dataSrcset =
        source.dataset && (source.dataset.srcset || source.dataset.srcSet);

      if (!srcset && dataSrcset) {
        source.setAttribute('srcset', dataSrcset);
      }
    });
  }

  function fixCardGalleries(root) {
    if (!root) return;

    root
      .querySelectorAll(
        '[ref="grid"] product-card .card-gallery slideshow-component'
      )
      .forEach(resetCardGallery);
  }

  function mutationTouchesProductResults(mutation) {
    const target =
      mutation.target instanceof Element
        ? mutation.target
        : mutation.target.parentElement;

    if (target && target.closest('[ref="grid"]')) {
      return true;
    }

    const changedNodes = [
      ...mutation.addedNodes,
      ...mutation.removedNodes,
    ];

    return changedNodes.some((node) => {
      if (!(node instanceof Element)) return false;

      if (
        node.matches(
          '[ref="grid"], [ref="cards[]"], product-card, slideshow-component, slideshow-slide'
        )
      ) {
        return true;
      }

      return Boolean(
        node.querySelector(
          '[ref="grid"], [ref="cards[]"], product-card, slideshow-component, slideshow-slide'
        )
      );
    });
  }

  function bootGalleryFix() {
    const resultsList = document.querySelector('results-list');
    if (!resultsList || resultsList.__galleryFixObserved) return;

    resultsList.__galleryFixObserved = true;

    let waitingForFilterResults = false;
    let settleTimer = null;
    let expiryTimer = null;

    const repairAfterMorph = () => {
      waitingForFilterResults = false;

      if (settleTimer) {
        clearTimeout(settleTimer);
        settleTimer = null;
      }

      if (expiryTimer) {
        clearTimeout(expiryTimer);
        expiryTimer = null;
      }

      /*
       * Two frames allow the DOM morph and the browser's layout calculations
       * to finish before resetting carousel visibility and scroll position.
       */
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          fixCardGalleries(resultsList);
        });
      });
    };

    const observer = new MutationObserver((mutations) => {
      if (!waitingForFilterResults) return;
      if (!mutations.some(mutationTouchesProductResults)) return;

      if (settleTimer) clearTimeout(settleTimer);

      /*
       * Morphing can produce several batches of mutations. Wait briefly until
       * they settle, then repair once.
       */
      settleTimer = setTimeout(repairAfterMorph, 100);
    });

    observer.observe(resultsList, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        'hidden',
        'aria-hidden',
        'src',
        'srcset',
        'data-last-page',
      ],
    });

    const handleFilterUpdate = () => {
      waitingForFilterResults = true;

      if (settleTimer) {
        clearTimeout(settleTimer);
        settleTimer = null;
      }

      if (expiryTimer) clearTimeout(expiryTimer);

      /*
       * Stop waiting if the network request fails or never updates the grid.
       */
      expiryTimer = setTimeout(() => {
        waitingForFilterResults = false;
        expiryTimer = null;
      }, 15000);
    };

    document.addEventListener('filter:update', handleFilterUpdate);

    // Normalise galleries once on the initial page load.
    fixCardGalleries(resultsList);

    window.addEventListener(
      'pagehide',
      () => {
        observer.disconnect();
        document.removeEventListener('filter:update', handleFilterUpdate);

        if (settleTimer) clearTimeout(settleTimer);
        if (expiryTimer) clearTimeout(expiryTimer);
      },
      { once: true }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootGalleryFix, {
      once: true,
    });
  } else {
    bootGalleryFix();
  }
})();

